<template>
  <div>
    <PartnerNav :logoUrl="partnerLogoUrl" />
    <div class="bg-sky-blue-900">
      <div class="standard-spacing py-16 md:flex space-x-8 items-center">
        <div class="w-full md:w-1/2">
          <h1 class="heading-1"><span class="font-sans font-light">Wellbeing & Performance,</span> Elevated</h1>
          <img src="https://dcctsvhz4yusz.cloudfront.net/de786eed-07a0-4755-8301-a51ecb084434.png" class="md:hidden" />
          <p class="mt-4 body-text">
            Hoap is delighted to offer CA ANZ members and their organisations complimentary Executive Leadership
            Coaching with our high-performance experts with the purchase of an annual Hoap subscription for your team.
          </p>
          <p class="mt-4 body-text">
            All teams are different, so our coaching will be tailored to the needs of your executive leadership group.
            Reach out to us today to learn more about this exclusive offer and how Hoap can elevate the wellbeing and
            performance of your team.
          </p>
          <div class="flex items-center space-x-4 mt-8">
            <HoapButton label="Buy Now" :url="config.public.buyUrl" external />
            <HoapButton label="Enquire Now" inverted url="/contact-us" />
          </div>
        </div>
        <div class="w-1/2 h-full hidden items-center md:flex">
          <img src="https://dcctsvhz4yusz.cloudfront.net/de786eed-07a0-4755-8301-a51ecb084434.png" class="w-full" />
        </div>
      </div>
    </div>
    <div class="bg-green-700">
      <div class="standard-spacing py-16">
        <HowHoapWorks altTitle="So, how does Hoap work?" />
      </div>
    </div>
    <div class="bg-gradient-lavender py-16">
      <div class="relative">
        <div class="standard-spacing">
          <HoapIcon icon="HoapLogo" class="h-12" />
          <div class="flex">
            <div class="w-full lg:w-1/2">
              <div class="text-xl lg:text-3xl pb-8">presents an exclusive webinar for CA ANZ members</div>
              <h2 class="heading-2">Unlocking sustained high performance through wellbeing</h2>
              <p class="pt-4 subheading">with special guests</p>
              <p class="subheading">Dr. Tom Patrick & James Begley</p>
              <HoapButton label="Watch playback" class="mt-8" @click="goToVideoContent" />
            </div>
            <div class="hidden lg:block xl:hidden absolute right-0 bottom-0 w-1/2">
              <img v-lazy="'https://dcctsvhz4yusz.cloudfront.net/d0f35a92-739c-4336-9947-38833ce20337.png'" />
            </div>
            <div class="hidden xl:block w-1/2">
              <img v-lazy="'https://dcctsvhz4yusz.cloudfront.net/bf5adfe4-8b81-43c8-94b6-57e080cc3967.png'" />
            </div>
          </div>
        </div>
      </div>

      <div ref="videoContent" class="standard-spacing pt-16">
        <div v-if="showVideo" class="h-48 md:h-128">
          <iframe
            src="https://player.vimeo.com/video/692960787?h=50faf2544b"
            width="100%"
            height="100%"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen />
        </div>
        <div v-else class="lg:flex lg:space-x-8">
          <form @submit.prevent="submit">
            <div class="grid grid-cols-2 gap-x-8">
              <HoapInput v-model="firstName" :disabled="loading" placeholder="First name *" required />
              <HoapInput v-model="lastName" :disabled="loading" placeholder="Last name *" required />
            </div>
            <HoapInput
              v-model="email"
              :disabled="loading"
              type="email"
              placeholder="Email *"
              required
              class="mt-8 w-full" />
            <div class="grid grid-cols-4 gap-x-8 mt-8">
              <HoapInput v-model="company" :disabled="loading" placeholder="Company" class="col-span-2 lg:col-span-3" />
              <HoapSelect
                v-model="role"
                :disabled="loading"
                :options="roleOptions"
                placeholder="Role *"
                required
                class="col-span-2 lg:col-span-1" />
            </div>
            <div class="mt-8 flex justify-end">
              <HoapIcon v-if="loading" icon="Loading" class="text-white w-12 h-12" />
              <p v-else-if="errorMessage" class="text-copper-700">{{ errorMessage }}</p>
              <HoapButton
                v-if="!loading"
                label="Submit and Watch"
                type="submit"
                :disabled="success || loading"
                inverted
                class="ml-8" />
            </div>
            <div class="text-right text-xs mt-4">
              This site is protected by reCAPTCHA and the Google
              <a href="https://policies.google.com/privacy" class="underline">Privacy Policy</a> and
              <a href="https://policies.google.com/terms" class="underline">Terms of Service</a> apply.
            </div>
          </form>
          <div class="w-full lg:w-1/2 mt-8 lg:mt-0">
            <div class="relative">
              <img v-lazy="'/img/chartered-accountants/webinar-welcome.png'" class="w-full" />
              <div class="absolute inset-0 bg-gray-400 bg-opacity-50 flex items-center justify-center">
                <HoapIcon icon="Lock" class="w-12 h-12 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-green-300">
      <div class="standard-spacing py-16">
        <h2 class="heading-2 text-center">Hear from some of our Hoap Family</h2>
        <Testimonial
          name="Mike Burfield"
          title="Director - Bentley's R&D Services"
          text="Very proud of our association with Hoap, which has been an invaluable tool for our team during COVID and beyond. Hoap allows us to drive connection when our team is remote, and continue to support our team through this solution."
          imageUrl="/img/chartered-accountants/mike.jpg"
          class="mt-16 max-w-screen-lg" />
        <Testimonial
          name="Andrew Fuss"
          title="Marketing Manager - Pickstar"
          text="I've had some really meaningful conversations with my manager as a result of using Hoap. There'd been a trend bubbling away that led to a really open and helpful discussion that allowed me to tell my manager I was struggling. Without this mechanism, this is something that's really difficult to address - especially if you're having a rough time. That conversation led to a really important change in the way I approach work and has had a significant, positive result on my mental health and on my work."
          imageUrl="https://dcctsvhz4yusz.cloudfront.net/49e700c7-3209-4dca-8f6e-d8aba50d4119.jpg"
          reversed
          class="mt-16 max-w-screen-lg" />
      </div>
    </div>
    <div
      class="py-12 md:py-24 bg-cover bg-center relative"
      :style="{
        backgroundImage: 'url(https://dcctsvhz4yusz.cloudfront.net/462bcef5-ced3-485f-ba76-9254dbddfadc.jpg)',
      }">
      <div class="absolute inset-0 pointer-events-none bg-scrim-full" />
      <div class="standard-spacing flex flex-col items-center">
        <h3 class="heading-2 z-10 text-center">
          <span class="font-sans font-light">Wellbeing & Performance,</span> Elevated.
        </h3>
        <HoapIcon icon="HoapLogo" class="mt-8 md:mt-16 h-8 md:h-12 z-10" />
        <HoapButton label="Learn More" url="/about" class="mt-8 md:mt-16 z-10" />
      </div>
    </div>
  </div>
  <PartnerNav :logoUrl="partnerLogoUrl" />
</template>

<script setup lang="ts">
  import { Ref } from 'vue'
  import Axios from 'axios'
  import { useReCaptcha } from 'vue-recaptcha-v3'

  import HowHoapWorks from '~~/layoutComponents/HowHoapWorks.vue'

  definePageMeta({
    layout: 'partner',
  })

  const config = useRuntimeConfig()

  const axios = Axios.create()
  const recaptcha = useReCaptcha()

  const roleOptions = [
    { value: 'leader', label: 'Leader' },
    { value: 'talent', label: 'Talent' },
  ]

  const videoContent: Ref<HTMLElement | null> = ref(null)

  const showVideo = ref(false)
  const firstName = ref('')
  const lastName = ref('')
  const email = ref('')
  const company = ref('')
  const role = ref('')

  const loading = ref(false)
  const success = ref(false)
  const errorMessage = ref('')

  const partnerLogoUrl = '/img/chartered-accountants/logo.png'

  onMounted(() => {
    showVideo.value = !!localStorage.getItem('submittedCaForm')
  })

  const goToVideoContent = () => {
    if (videoContent.value) {
      window.scrollTo({ top: videoContent.value.offsetTop, behavior: 'smooth' })
    }
  }

  const submit = async () => {
    errorMessage.value = ''
    success.value = false
    loading.value = true

    await recaptcha?.recaptchaLoaded()

    const recaptchaToken = await recaptcha?.executeRecaptcha('contact')

    try {
      await axios.post('/api/submit-ca-form', {
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        company: company.value,
        role: role.value,
        from: 'ca-webinar-form',
        recaptchaToken,
      })

      success.value = true
      localStorage.setItem('submittedCaForm', 'true')
      showVideo.value = true
    } catch (e: any) {
      errorMessage.value = `${e.response.status} ${e.response.statusText}`
    } finally {
      loading.value = false
    }
  }

  setMetadata({ pageTitle: 'Chartered Accountants', robots: 'noindex, nofollow' })
</script>
